import { escapeHtml } from './dom.js';

export function formatPlaygroundErrorHtml(error) {
    if (typeof error === 'string') {
        return `<li class="playground-error-item"><p class="playground-error-message">${escapeHtml(error)}</p></li>`;
    }
    if (!error || typeof error !== 'object') {
        return `<li class="playground-error-item"><p class="playground-error-message">${escapeHtml(String(error))}</p></li>`;
    }

    const code = error.code
        ? `<div class="playground-error-code">${escapeHtml(error.code)}</div>`
        : '';
    const message = error.message
        ? `<p class="playground-error-message">${escapeHtml(error.message).replace(/\n/g, '<br>')}</p>`
        : '';
    const hint = error.hint
        ? `<p class="playground-error-hint"><span class="playground-error-label">Hint:</span> ${escapeHtml(error.hint)}</p>`
        : '';
    let location = '';
    if (error.range && error.range.file) {
        const loc = error.range.start_line != null
            ? `${error.range.file}:${error.range.start_line}`
            : error.range.file;
        location = `<p class="playground-error-location"><span class="playground-error-label">At:</span> ${escapeHtml(loc)}</p>`;
    } else if (error.path) {
        location = `<p class="playground-error-location"><span class="playground-error-label">Field:</span> ${escapeHtml(error.path)}</p>`;
    }
    const docUrl = error.doc_url;
    const safeDocUrl =
        typeof docUrl === 'string' &&
        (docUrl.startsWith('https://') || docUrl.startsWith('http://'))
            ? docUrl
            : '';
    const docs = safeDocUrl
        ? `<p class="playground-error-docs"><a href="${escapeHtml(safeDocUrl)}" target="_blank" rel="noopener noreferrer">Documentation</a></p>`
        : '';

    return `<li class="playground-error-item">${code}${message}${hint}${location}${docs}</li>`;
}

export function buildErrorContainerHtml(errors) {
    const items = (errors || [])
        .map(error => formatPlaygroundErrorHtml(error))
        .join('');
    return `
        <div class="error-container">
            <h3>Errors</h3>
            <ul class="playground-error-list">
                ${items}
            </ul>
        </div>
    `;
}

// Render-time warnings (the 14-detector corpus: band-width-too-narrow,
// flat-cols-overflow, etc) are non-fatal — the chart still rendered — so they
// get a collapsed, unobtrusive sibling to the error container rather than a
// blocking panel. Diagnostic shape (as serialized here): {chart, code, field,
// message, fix, fields}. `fields` also carries programmatic-only metadata
// (chart_id, severity, confidence) — only detail/evidence are human-readable.
export function formatPlaygroundWarningHtml(warning) {
    if (!warning || typeof warning !== 'object') {
        return `<li class="playground-warning-item"><p class="playground-warning-message">${escapeHtml(String(warning))}</p></li>`;
    }

    const chartPrefix = warning.chart ? `${escapeHtml(warning.chart)}: ` : '';
    const code = warning.code
        ? `<div class="playground-warning-code">${escapeHtml(warning.code)}</div>`
        : '';
    const message = warning.message
        ? `<p class="playground-warning-message">${chartPrefix}${escapeHtml(warning.message).replace(/\n/g, '<br>')}</p>`
        : '';
    const field = warning.field
        ? `<p class="playground-warning-location"><span class="playground-warning-label">Field:</span> ${escapeHtml(warning.field)}</p>`
        : '';
    const fields = warning.fields || {};
    const detail = fields.detail
        ? `<p class="playground-warning-detail"><span class="playground-warning-label">Detail:</span> ${escapeHtml(fields.detail)}</p>`
        : '';
    const evidence = fields.evidence && fields.evidence.length
        ? `<p class="playground-warning-evidence"><span class="playground-warning-label">Evidence:</span> ${escapeHtml(fields.evidence.join('; '))}</p>`
        : '';
    const fix = warning.fix
        ? `<p class="playground-warning-fix"><span class="playground-warning-label">Fix:</span> ${escapeHtml(warning.fix).replace(/\n/g, '<br>')}</p>`
        : '';

    return `<li class="playground-warning-item">${code}${message}${field}${detail}${evidence}${fix}</li>`;
}

function buildDetailsStripHtml(containerClass, summaryClass, listClass, noun, items) {
    if (items.length === 0) return '';
    const label = items.length === 1 ? `1 ${noun}` : `${items.length} ${noun}s`;
    return `
        <details class="${containerClass}">
            <summary class="${summaryClass}">&#9888; ${label}</summary>
            <ul class="${listClass}">
                ${items.join('')}
            </ul>
        </details>
    `;
}

export function buildWarningsStripHtml(warnings) {
    return buildDetailsStripHtml(
        'warnings-strip', 'warnings-strip-summary', 'playground-warning-list', 'warning',
        (warnings || []).map(formatPlaygroundWarningHtml),
    );
}

// Unranged diagnostics from applyDiagnostics(): errors/warnings the editor
// could not squiggle (null range, or cross-file range). Shown below rendered
// content so they are not silently dropped when the board partially renders.
export function buildUnrangedDiagnosticsStripHtml(diagnostics) {
    return buildDetailsStripHtml(
        'unranged-diagnostics-strip', 'unranged-diagnostics-strip-summary',
        'playground-error-list', 'problem',
        (diagnostics || []).map(formatPlaygroundErrorHtml),
    );
}

export function updatePreviewStatus(status, className) {
    const statusEl = document.getElementById('preview-status');
    statusEl.textContent = status;
    statusEl.className = `status-indicator status-${className}`;
}
